import { useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ChildExperienceLoader } from '@/features/experiences/components/ChildExperienceLoader';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useDeleteChildExperience } from '@/features/experiences/hooks/useDeleteChildExperience';
import { useUpdateExperience } from '@/features/experiences/hooks/useUpdateExperience';
import { useActiveView } from '@/features/planner/hooks/useActiveView';
import { Box, Flex } from '@/ui';
import { useAddViewGate } from '../useAddViewGate';
import { useCustomizeMode } from '../useCustomizeMode';
import { AddViewPicker } from './AddViewPicker';
import { ViewSelector } from './ViewSelector';
import { ViewSettingsSections } from './ViewSettingsSections';

export const ViewsPanel = () => {
  const { experience } = usePageExperienceContext();
  const { activeChildId, setActiveView } = useActiveView(experience);
  const { openAddView, openChangeLayout, addViewMode } = useCustomizeMode();
  const { runGatedAddView } = useAddViewGate();
  const { deleteChildExperience, isPending: isRemoving } =
    useDeleteChildExperience(experience.id);
  const { updateExperience } = useUpdateExperience();
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);

  const hasChildren = experience.children.length > 0;

  // This panel's context is the parent planner, so the update targets the
  // planner without an experienceId override. The pointer lives on the parent,
  // which is what makes "only one default" true by construction.
  const handleToggleDefault = () => {
    if (!activeChildId) return;
    const isDefault = experience.defaultChildExperienceId === activeChildId;
    updateExperience({
      defaultChildExperienceId: isDefault ? null : activeChildId,
    });
  };

  const handleRemoveView = () => {
    if (!activeChildId) return;
    setIsConfirmingRemove(true);
  };

  const handleConfirmRemove = () => {
    if (!activeChildId) return;
    deleteChildExperience(activeChildId, {
      onSuccess: () => setIsConfirmingRemove(false),
    });
  };

  // When the picker is open, render it inline in the rail (no fullscreen overlay).
  // Confirm/cancel live in the top bar (useAddViewFlow); the picker only writes the
  // selection to the URL.
  if (addViewMode !== null) {
    const picker = <AddViewPicker mode={addViewMode} />;

    return (
      <Flex direction="column" flex={1} minH={0}>
        {addViewMode === 'change' && activeChildId ? (
          // Re-scopes context to the active child so the picker reads that child's
          // `type` to lock the kind (interactive vs printable) — the committed
          // one, hence applyPreview={false}: the picker must stay anchored to the
          // view being changed, not to the candidate it is previewing.
          <ChildExperienceLoader
            applyPreview={false}
            childExperienceId={activeChildId}
            variant="inline"
          >
            {picker}
          </ChildExperienceLoader>
        ) : (
          picker
        )}
      </Flex>
    );
  }

  return (
    <Box flex={1} overflowY="auto" p={4} pb={10}>
      {hasChildren ? (
        <>
          <ViewSelector
            activeChildId={activeChildId}
            defaultChildId={experience.defaultChildExperienceId}
            isRemoving={isRemoving}
            views={experience.children}
            onAddView={() => runGatedAddView(openAddView)}
            onChangeLayout={openChangeLayout}
            onRemoveView={handleRemoveView}
            onSelectView={setActiveView}
            onToggleDefault={handleToggleDefault}
          />
          {activeChildId && (
            // Re-scopes PageExperienceContext (and useUpdateExperience) to the active
            // child so the sections below edit that view, not the parent planner.
            // applyPreview={false}: these are the controls, so they keep reporting
            // the committed value while the page renders the hovered preview —
            // otherwise a hovered toggle flips itself, and a hovered display type
            // adds and removes rows under the cursor.
            <ChildExperienceLoader
              applyPreview={false}
              childExperienceId={activeChildId}
              variant="inline"
            >
              <ViewSettingsSections />
            </ChildExperienceLoader>
          )}
          <ConfirmDialog
            confirmButtonText="Remove view"
            isLoading={isRemoving}
            isOpen={isConfirmingRemove}
            message="This view and its settings will be permanently removed. This can't be undone."
            title="Remove this view?"
            onCancel={() => setIsConfirmingRemove(false)}
            onConfirm={handleConfirmRemove}
          />
        </>
      ) : (
        // Non-planner / single experience: sections edit the experience directly.
        <ViewSettingsSections />
      )}
    </Box>
  );
};
